'use client';

import type { ComponentProps } from 'react';
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog';
import { useVisibleViewportVars } from '#hooks/use-visible-viewport-vars.ts';
import { cn } from '#lib/utils.ts';
import { XIcon } from 'lucide-react';

export type DialogPortalContainer = NonNullable<DialogPrimitive.Portal.Props['container']>;

function Dialog(props: DialogPrimitive.Root.Props) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger(props: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal(props: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose(props: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({ className, ...props }: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      className={cn(
        'fixed inset-0 z-50 bg-overlay duration-200 data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0',
        className,
      )}
      {...props}
    />
  );
}

type DialogContentSize = 'sm' | 'md' | 'lg' | 'xl' | 'immersive' | 'fullscreen';

const dialogContentSizeClasses: Record<DialogContentSize, string> = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-xl',
  lg: 'sm:max-w-3xl',
  xl: 'sm:max-w-5xl',
  immersive: 'flex h-[92vh] max-h-[92vh] w-[94vw] max-w-[94vw] flex-col gap-0 overflow-hidden p-0 sm:max-w-[94vw]',
  fullscreen:
    'fixed top-0 right-0 bottom-0 left-0 flex h-screen max-h-screen w-screen max-w-none translate-x-0 translate-y-0 flex-col overflow-hidden rounded-none border-0 p-0 sm:max-w-none sm:rounded-none',
};

function DialogContent({
  className,
  children,
  showCloseButton = true,
  portalContainer,
  overlayClassName,
  overlayForceRender = false,
  size = 'md',
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean;
  portalContainer?: DialogPortalContainer;
  overlayClassName?: string;
  /** Render the scrim even when this dialog is nested in another dialog (Base UI suppresses it otherwise). */
  overlayForceRender?: boolean;
  size?: DialogContentSize;
}) {
  return (
    <DialogPrimitive.Portal container={portalContainer} data-slot="dialog-portal">
      <DialogOverlay className={overlayClassName} forceRender={overlayForceRender} />
      <DialogPrimitive.Popup
        data-slot="dialog-content"
        className={cn(
          'bg-card fixed top-[calc(var(--sk-visible-viewport-top,0px)_+_var(--sk-visible-viewport-height,100%)/2)] left-[50%] z-50 grid w-[calc(100%-2rem)] max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 outline-none data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
          dialogContentSizeClasses[size],
          className,
        )}
        {...props}
      >
        <VisibleViewportVarsSync />
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            // z-20 must stay above any consumer-supplied positioned/sticky content inside DialogContent
            // (e.g. a `sticky` DialogHeader with its own z-index) — without it, that content paints
            // over this button (an explicit z-index always wins over this button's default z-index:auto,
            // regardless of DOM order) and the close button becomes invisible/unclickable.
            className="ring-offset-background focus-visible:ring-ring absolute top-4 right-4 z-20 rounded-xs text-secondary transition-opacity hover:bg-component-hover hover:text-dark focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 hover:cursor-pointer"
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Popup>
    </DialogPrimitive.Portal>
  );
}

function VisibleViewportVarsSync() {
  useVisibleViewportVars();
  return null;
}

function DialogHeader({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex flex-col gap-2 text-center sm:text-left', className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
}

function DialogTitle({ className, ...props }: ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('text-lg leading-none font-semibold', className)}
      {...props}
    />
  );
}

function DialogDescription({ className, ...props }: ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-dark text-sm', className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
